import { useNavigate, useParams } from "react-router-dom"
import { useGetDatasetVisualizationsResultsetDatasetVisualizationResultsetIdGet, useGetResultsetsResultsetGet } from "../../../api/generated/fastAPI";
import { useEffect, useState, MouseEvent, useMemo } from "react";
import { ResultsetVisualizationType } from "../components/ResultsetVisualizations";
import SinglePageHeader from "../../../components/Page/SinglePageHeader";
import { Dataset, ModelFamily, Resultset } from "../../../api/models";
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import Plot from "react-plotly.js";
import MoleculeStructure from "../../../components/Data/MoleculeStructure";
import { exportFileExtensions } from "../../../utils/supplementalData";
import useMessage from "../../../hooks/useMessage";
import SnackBar from "../../../components/Alerts/SnackBar";
import { Skeleton } from "@mui/material";
import { formatDisplayedResultsetVisualizationMetadata, MetadataMenuItemType } from "../../../utils/dataFormattingFns";
import UpdatedMetadataMenu from "../../../components/Menus&Dropdowns/UpdatedMetadataMenu";
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown";
import useDownload from "../../../hooks/endpoints/useDownload";
import { customInstance } from "../../../api/custom-instance";
import { config, layout_tsne_umap_pca } from "../../../utils/plot";

const trainingSetColors = [
    '#ff7f0e',  // safety orange
    '#2ca02c',  // cooked asparagus green
    '#d62728',  // brick red
    '#9467bd',  // muted purple
    '#8c564b',  // chestnut brown
    '#e377c2',  // raspberry yogurt pink
    '#7f7f7f',  // middle gray
    '#bcbd22',  // curry yellow-green
    '#17becf'   // blue-teal
];

export function getRandomColorForLegend() {
  const isAccessible = (hexColor: string) => {
    const white = '#FFFFFF';
    const lum1 = getLuminance(hexColor);
    const lum2 = getLuminance(white);
    const contrastRatio = (Math.max(lum1, lum2) + 0.05) / (Math.min(lum1, lum2) + 0.05);
    return contrastRatio >= 4.5; // WCAG AA minimum contrast
  };

  const getLuminance = (hex: string) => {
    const [r, g, b] = hexToRgb(hex).map(c => {
      c /= 255;
      return c <= 0.03928
        ? c / 12.92
        : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const hexToRgb = (hex: string) => {
    hex = hex.replace('#', '');
    const bigint = parseInt(hex, 16);
    return [
      (bigint >> 16) & 255,
      (bigint >> 8) & 255,
      bigint & 255,
    ];
  };

  // Keep generating until contrast is acceptable
  let color;
  let isUnique = false;
  do {
    color = '#';
    const letters = '0123456789ABCDEF';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }

    if (!trainingSetColors.includes(color.toUpperCase())) {
        isUnique = true;
    }
  } while (!isAccessible(color) || !isUnique);

  return color;
}


const getParamMetadata = (vis: ResultsetVisualizationType) => {
    const fingerprintType = vis.metadata.dataset_visualization_metadata.airflow_dag_run_conf.fp_type;
    const { method_type, params } = vis.metadata.dataset_visualization_metadata.visualization_method_metadata;

    let fields: { label: string, value: string }[] = fingerprintType ? [{
        label: 'Fingerprint',
        value: (fingerprintType as string).toUpperCase(),
    }] : [];

    if (method_type === 'tsne') {
        fields = fields.concat([
            {
                label: 'Perplexity',
                value: params.perplexity ? params.perplexity.toString() : 'No Value',
            },
            {
                label: 'Method',
                value: params.method ? params.method.toString() : 'No Value',
            }
        ]);
    } else if (method_type === 'pca') {
        fields = fields.concat([
            {
                label: 'Whiten',
                value: params.whiten !== undefined ? params.whiten === true ? 'True' : 'False' : 'No Value',
            },
            {
                label: 'SVD Solver',
                value: params.svd_solver ? params.svd_solver.toString() : 'No Value',
            }
        ]);
    } else {
        fields = fields.concat([
            {
                label: 'Nearest Neighbors',
                value: params.n_neighbors ? params.n_neighbors.toString() : 'No Value',
            },
            {
                label: 'Metric',
                value: params.metric ? params.metric.toString() : 'No Value',
            },
            {
                label: 'Init',
                value: params.init ? params.init.toString() : 'No Value',
            }
        ]);
    };

    return fields;
}

const fetchTestAndTrainSets = async (
    vis: ResultsetVisualizationType,
) => {
    const { test_keys, train_keys, metadata } = vis;
    const { is_single_prediction_visualization } = metadata;

    const testSets: Dataset[] = [];
    const trainSets: ModelFamily[] = [];

    try {

        if (test_keys.length > 0 && !is_single_prediction_visualization) {
            const promises = test_keys.map(async (hash) => {
                const response = await customInstance({
                    url: `dataset/?dataset_hash=${hash}&include_dataset_metadata=false&include_airflow_metadata=false`,
                    method: 'GET',
                });
                const dataset = response as unknown as Dataset;
                if (dataset) return testSets.push(dataset);
            });
            await Promise.all(promises);
        };

        if (train_keys.length > 0) {
            const promises = train_keys.map(async (id) => {
                const response = await customInstance({
                    url: `model_family/?model_family_id=${id}&include_model_family_metadata=false&include_model_stats=false&include_airflow_metadata=false`,
                    method: 'GET',
                });
                const modelFamily = response as unknown as ModelFamily;
                if (modelFamily) return trainSets.push(modelFamily);
            });

            await Promise.all(promises);
        }

    } catch (err:any) {
        console.log(err);
    }

    return {
        testSets,
        trainSets,
    }
};


const formatPlotData = (
    vis: ResultsetVisualizationType, 
    testSets: Dataset[],
    trainSets: ModelFamily[],
    selectedPoint: {traceIndex: number | null, pointIndex: number | null},
    traceVisibility: boolean[],
) => {
    const { graph_data, metadata } = vis;
    const { is_single_prediction_visualization } = metadata;

    let testTrace = {};
    let trainTraces: any[] = [];

    let trainCount = 0;

    for (const [key, graphData] of Object.entries(graph_data)) {
        const trainIndex = trainCount;
        const { x, y } = graphData;
        const smiles = graphData["structure/SMILES"];

        let setName = '';
        let isTestKey = false;

        if (isNaN(Number(key))) {
            if (testSets.length > 0 && !is_single_prediction_visualization) {
                const matchingTestSet = testSets.find(set => set.file_hash === key);
                if (matchingTestSet) {
                    setName = matchingTestSet.name ?? 'Test Set';
                    isTestKey = true;
                }
            } else {
                setName = 'Test Set';
                isTestKey = true;
            }
        } else {
            const matchingTrainingSet = trainSets.find(set => set.id === Number(key));
            if (matchingTrainingSet) {
                setName = matchingTrainingSet.name ?? 'Training Set';
            }
        }


        const moleculeInfo: any[] = [];

        smiles.forEach(smile => {
            const molObj = {
                molecule_smiles: smile,
                set_type: isTestKey ? 'test' : 'train',
                name: setName,
                color: isTestKey ? '#1f77b4' : trainingSetColors[trainIndex] ?? getRandomColorForLegend(),
            };
            moleculeInfo.push(molObj);
        });

        if (isTestKey) {
            testTrace = {
                x,
                y,
                mode: 'markers',
                type: 'scatter',
                name: 'Test',
                marker: { color: '#1f77b4' },
                selectedpoints: selectedPoint.traceIndex === 0 ? [selectedPoint.pointIndex] : null,
                selected: {
                    marker: { size: 12, color: '#FF00FF' },  // Style for selected points
                },
                visible: traceVisibility[0] ? true : 'legendonly',
                customdata: moleculeInfo,
            }
        } else {
            const modelTrace = {
                x,
                y,
                mode: 'markers',
                type: 'scatter',
                name: 'Train',
                marker: { color: trainingSetColors[trainCount] },
                selectedpoints: selectedPoint.traceIndex === trainCount+1 ? [selectedPoint.pointIndex] : null,
                selected: {
                    marker: { size: 12, color: '#FF00FF' },  // Style for selected points
                },
                visible: traceVisibility[trainCount+1] ? true : 'legendonly',
                customdata: moleculeInfo,
            }

            trainTraces.push(modelTrace);

            trainCount += 1;
        }
    }

    const data = [testTrace].concat(trainTraces);
    return data;
};

const application = process.env.REACT_APP_APPLICATION;


export default function IndividualResultsetVisualization() {
    const { resultset_id, id } = useParams();
    const navigate = useNavigate();

    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    const { data: visData, isLoading: isVisLoading } = useGetDatasetVisualizationsResultsetDatasetVisualizationResultsetIdGet(
        resultset_id ? Number(resultset_id) : 0,
        { include_smiles: application === 'assay-central' ? true : false }
    );
    const { data: rsData, isLoading: isRsLoading } = useGetResultsetsResultsetGet({ resultset_id });
    const [ isDataLoading, setIsDataLoading ] = useState(false);
    const [ testSets, setTestSets ] = useState<Dataset[]>([]);
    const [ trainingSets, setTrainingSets ] = useState<ModelFamily[]>([]);

    const [ visualizationMetadata, setVisualizationMetadata ] = useState<MetadataMenuItemType[]>([]);

    const [ selectedMolecule, setSelectedMolecule ] = useState<{molecule_smiles?: string, set_type: string, name: string, color: string} | null>(null);
    const [ selectedPoint, setSelectedPoint ] = useState<{traceIndex: number | null, pointIndex: number | null}>({traceIndex: null, pointIndex: null});
    const [ traceVisibility, setTraceVisibility ] = useState<boolean[]>([]);

    const [ isLoading, setIsLoading ] = useState(false);
    const { downloadResultsetVisualization } = useDownload();

    const visualization = useMemo(() => {
        if (!visData) return null;
        const matchingVis = (visData as unknown as ResultsetVisualizationType[]).find(vis => vis.metadata.file_hash === id);
        if (!matchingVis) return null;
        return matchingVis;
    }, [visData, id]);

    const resultset = useMemo(() => {
        if (!rsData) return null;
        return rsData as unknown as Resultset;
    }, [rsData]);

    useEffect(() => {
        if (isVisLoading || isRsLoading || isDataLoading) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [
        isVisLoading,
        isRsLoading,
        isDataLoading,
    ]);

    useEffect(() => {
        const fetchVisMetadataAndSets = async (vis: ResultsetVisualizationType) => {

            setIsDataLoading(true);

            const metadata = await formatDisplayedResultsetVisualizationMetadata(vis);
            const { testSets: testingSets, trainSets } = await fetchTestAndTrainSets(vis);

            setVisualizationMetadata(metadata);
            setTestSets(testingSets);
            setTrainingSets(trainSets);

            setIsDataLoading(false);
        }


        if (visualization) {
            fetchVisMetadataAndSets(visualization);
            const graphDataKeys = Object.keys(visualization.graph_data);
            setTraceVisibility(graphDataKeys.map(key => true));
        }
    }, [visualization]);
    
    const plotData = useMemo(() => {
        if (visualization && trainingSets.length > 0) {
            return formatPlotData(
                visualization, 
                testSets, 
                trainingSets,
                selectedPoint,
                traceVisibility
            ) as any[];
        } else {
            return [];
        }
    }, [
        visualization,
        testSets,
        trainingSets,
        selectedPoint,
        traceVisibility
    ]);


    const getMolecule = (e:any) => {
        const point = e.points[0];
        setSelectedMolecule(point.customdata);

        setSelectedPoint({ traceIndex: point.curveNumber, pointIndex: point.pointIndex })
    };

    const toggleTraceVisibility = (index: number) => {
        const newVisibility = [...traceVisibility];
        newVisibility[index] = !newVisibility[index]; // Toggle the visibility
        setTraceVisibility(newVisibility);
    }

    const dropdownOptions: DropdownOption[] = [
        {
            label: 'Export',
            action: (e:MouseEvent<HTMLButtonElement>) => {},
            moreOptions: (exportFileExtensions.filter(ext => ext.label !== 'SDF')).map(ext => (
                {
                    label: ext.label,
                    action: async (e: MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        if (resultset && visualization) {
                            try {
                                await downloadResultsetVisualization(
                                    ext.file_type,
                                    visualization,
                                    resultset,
                                );
                                setPopupMessage('You have successfully downloaded the selected visualization.', true);
                            } catch (err:any) {
                                console.log(err);
                                setPopupMessage('There was an error downloading this visualization. Please try again.', false);
                            }
                        }
                    }
                }
            ))
        },
    ];


    return (
        <div className="p-10 h-full flex flex-col justify-start items-stretch gap-4">
            <SinglePageHeader 
                id='single-vis-header' 
                label={visualization ? visualization.metadata.dataset_visualization_metadata.visualization_method_metadata.method_type.toUpperCase() : ''} 
                isLoading={isLoading} 
                pages={[
                    {
                        label: 'Resultsets',
                        action: (e: MouseEvent<HTMLButtonElement>) => {navigate('/resultsets')}
                    },
                    {
                        label: resultset ? resultset.name! : '',
                        action: (e: MouseEvent<HTMLButtonElement>) => {navigate(`/resultsets/${resultset_id}`)}
                    },
                    {
                        label: visualization ? visualization.metadata.dataset_visualization_metadata.visualization_method_metadata.method_type.toUpperCase() : '',
                    }
                ]}
                itemMetadata={visualization ? getParamMetadata(visualization) : []}
            >
                <NestedDropdown
                    dir="right"
                    btn={{
                        label: 'Options',
                        type: 'primary',
                        styling: 'solid',
                        id: 'single-vis-options'
                    }}
                    options={dropdownOptions}
                />
                {visualizationMetadata.length > 0 && (
                    <UpdatedMetadataMenu 
                        menuLabel='Visualization Metadata'
                        menuItems={visualizationMetadata}
                        location="page"
                    />
                )}
            </SinglePageHeader>

            <div className="w-full flex justify-between items-start gap-6 mt-10">
                {isLoading ? (
                    <div className="w-full aspect-square grow"><Skeleton variant='rounded' width='100%' height='100%'/></div>
                ) : plotData.length > 0 ? (
                    <div className={`w-full grow rounded-lg bg-white border border-outline flex flex-col justify-start items-start gap-6 h-full p-8`}>
                        <Plot 
                            onClick={getMolecule} 
                            layout={layout_tsne_umap_pca()} 
                            data={plotData} 
                            config={config} 
                            className='w-full aspect-square'
                        />
                    </div>
                ) : (
                    <></>
                )}
                

                <div className="w-[400px] flex flex-col justify-start items-stretch gap-6">
                    <div className='w-full flex flex-col gap-2 justify-center items-start'>
                        <label className={`text-[12px] font-semibold text-default`}>Legend</label>

                        {isLoading ? (
                            <div className="w-full h-[150px]"><Skeleton variant='rounded' width='100%' height='100%'/></div>
                        ) : plotData.length > 0 ? (
                            <div className="w-full flex flex-col bg-white border border-outline rounded-lg overflow-hidden">
                                <button onClick={(e:any) => toggleTraceVisibility(0)} className={`w-full flex justify-start items-center gap-4 p-4 ${traceVisibility[0] ? 'opacity-100' : 'opacity-30'}`}>
                                    <div className={`w-[20px] h-[20px] aspect-square bg-[#1f77b4] rounded-sm`}></div>
                                    <p className={`font-medium truncate text-default`}>Test - {plotData[0].customdata[0].name}</p>
                                </button>
                                {plotData.filter((d:any) => d.customdata[0].set_type === 'train').length > 0 && plotData.filter((d:any) => d.customdata[0].set_type === 'train').map((train:any, i: number) => {
                                    return (
                                    <button onClick={(e:any) => toggleTraceVisibility(i+1)} className={`w-full flex justify-start items-center gap-4 p-4 ${traceVisibility[i+1] ? 'opacity-100' : 'opacity-30'}`}>
                                        <div style={{backgroundColor: train.customdata[0].color}} className={`w-[20px] h-[20px] aspect-square rounded-sm`}></div>
                                        <p className={`font-medium break-words truncate text-default`}>Train - {train.customdata[0].name}</p>
                                    </button>
                                    )
                                })}
                                
                            </div>
                        ) : (
                            <></>
                        )}      
                    </div>

                    <div className='w-full flex flex-col gap-2 justify-center items-start'>
                        <label className={`text-[12px] font-semibold text-default`}>Selected Compound</label>
                        {isLoading ? (
                            <div className="w-full aspect-square"><Skeleton variant='rounded' width='100%' height='100%'/></div>
                        ) : plotData.length > 0 ? (
                            <div className={`w-full relative overflow-hidden rounded-xl bg-white border border-outline flex flex-col justify-start items-stretch`}>
                                {!selectedMolecule ? (
                                    <div className="w-full flex justify-center items-center aspect-square">
                                        <p className="text-quaternary font-semibold text-center text-[18px] max-w-[300px]">Select a point to view molecule information</p>
                                    </div>
                                ) : selectedMolecule && selectedMolecule.molecule_smiles ? (
                                    <>
                                        <div className="w-full p-3 flex justify-center items-center">
                                            <MoleculeStructure smiles={selectedMolecule.molecule_smiles} height={200} width={200}/>
                                        </div>
                                        <div className="w-full grow flex flex-col justify-start items-stretch gap-6 p-6 border-t border-outline">
                                            <div className="grow">
                                                <p className={`font-semibold text-default text-[16px] max-w-full truncate`}>
                                                    {selectedMolecule.molecule_smiles}
                                                </p>
                                            </div>
                                            <div className="w-full flex flex-col justify-start items-start gap-2">
                                                <p className="text-[12px] font-semibold text-disable">Set Name</p>
                                                <p className="text-default max-w-full truncate">{selectedMolecule.name}</p>
                                            </div>
                                            <div className="w-full flex flex-col justify-start items-start gap-2">
                                                <p className="text-[12px] font-semibold text-disable">Set Type</p>
                                                <div style={{backgroundColor: selectedMolecule.color}} className="flex justify-center items-center gap-2 py-1 px-3 text-white font-semibold text-[12px] rounded-lg">
                                                    {selectedMolecule.set_type}
                                                </div>
                                            </div>
                                        </div>
                                        {/* <DetailsModal 
                                            data={data}
                                            isGridItem
                                            setPopupMessage={setPopupMessage}
                                        /> */}
                                    </>
                                ) : (
                                    <div className="w-full flex justify-center items-center aspect-square">
                                        <p className="text-quaternary font-semibold text-center text-[18px] max-w-[300px]">Molecule information unavailable</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <></>
                        )}
                        
                    </div>
                </div>
            </div>

            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  
        </div>
    )
}